import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Sparkles,
  Quote,
  Heart,
  Brain,
  Shield,
  Target,
  Compass,
  Layers,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  HelpCircle,
} from "lucide-react";

export default function WhyChooseSection() {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();

  /* ---------- Visuels ---------- */
  const BG_URL = "/Images/riad.avif";
  const COACH_IMG = "/Images/working.jpg";
  const FALLBACK =
    "https://images.unsplash.com/photo-1524253482453-3fed8d2fe12b?auto=format&fit=crop&w=1600&q=60";

  /* ---------- Données i18n ---------- */
  const H_TITLE = t("whychoose.title");
  const TRANSITION = t("whychoose.transition");
  const N_P1 = t("whychoose.narr.p1");
  const N_P2 = t("whychoose.narr.p2");
  const N_BULLETS = [
    t("whychoose.narr.li1"),
    t("whychoose.narr.li2"),
    t("whychoose.narr.li3"),
  ];

  /* ---------- Carrousel : 1 carte par écran ---------- */
  const cards = useMemo(
    () => [
      {
        key: "method",
        icon: Brain,
        badge: t("whychoose.card.method.badge"),
        title: t("whychoose.card.method.title"),
        intro: [
          t("whychoose.card.method.i1"),
          t("whychoose.card.method.i2"),
        ],
        listTitle: t("whychoose.card.method.listTitle"),
        list: [
          t("whychoose.card.method.l1"),
          t("whychoose.card.method.l2"),
          t("whychoose.card.method.l3"),
          t("whychoose.card.method.l4"),
        ],
        tint: "text-warm-sage",
        bg: "from-warm-sage/10 to-cream-white",
        border: "border-warm-sage/20",
      },
      {
        key: "process",
        icon: Compass,
        badge: t("whychoose.card.process.badge"),
        title: t("whychoose.card.process.title"),
        steps: [
          t("whychoose.card.process.s1"),
          t("whychoose.card.process.s2"),
          t("whychoose.card.process.s3"),
          t("whychoose.card.process.s4"),
        ],
        note: t("whychoose.card.process.note"),
        tint: "text-soft-taupe",
        bg: "from-soft-taupe/10 to-cream-white",
        border: "border-soft-taupe/20",
      },
      {
        key: "tools",
        icon: Layers,
        badge: t("whychoose.card.tools.badge"),
        title: t("whychoose.card.tools.title"),
        list: [
          t("whychoose.card.tools.l1"),
          t("whychoose.card.tools.l2"),
          t("whychoose.card.tools.l3"),
          t("whychoose.card.tools.l4"),
          t("whychoose.card.tools.l5"),
          t("whychoose.card.tools.l6"),
        ],
        tint: "text-rich-chocolate",
        bg: "from-rich-chocolate/10 to-cream-white",
        border: "border-rich-chocolate/20",
      },
    ],
    [t]
  );

  const trackRef = useRef(null);
  const scrollBy = (dirX) => {
    const el = trackRef.current;
    if (!el) return;
    const delta = dirX * el.clientWidth;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  /* ---------- Micro-quiz (4 Q) ---------- */
  const quizItems = useMemo(
    () => [
      t("whychoose.quiz.q1"),
      t("whychoose.quiz.q2"),
      t("whychoose.quiz.q3"),
      t("whychoose.quiz.q4"),
    ],
    [t]
  );

  const [answers, setAnswers] = useState(Array(quizItems.length).fill(null));
  const answered = answers.filter((a) => a !== null).length;
  const yesCount = answers.filter((a) => a === true).length;
  const progress = Math.round((answered / quizItems.length) * 100);

  const setAnswer = (i, v) =>
    setAnswers((prev) => prev.map((x, idx) => (idx === i ? v : x)));

  // Statut « signal intérieur »
  const signal =
    yesCount <= 1
      ? { label: t("whychoose.signal.green"), badge: "bg-warm-sage text-cream-white" }
      : yesCount <= 3
      ? { label: t("whychoose.signal.amber"), badge: "bg-soft-taupe text-charcoal" }
      : { label: t("whychoose.signal.red"), badge: "bg-rich-chocolate text-cream-white" };

  const CTA_LABEL = t("whychoose.cta");

  return (
    <section id="philosophie" className=" overflow-hidden">
      {/* Fond + voile lisible */}
      <div className="absolute inset-0 -z-10">
        <img
          src={BG_URL}
          onError={(e) => (e.currentTarget.src = FALLBACK)}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-cream-white/92 via-cream-white/88 to-soft-gray/92" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header citation */}
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-warm-sage" />
            <span className="text-xs tracking-widest uppercase text-warm-gray">
              {t("whychoose.kicker")}
            </span>
            <Sparkles className="h-5 w-5 text-soft-taupe" />
          </div>

          <h2 className="text-[clamp(22px,5.2vw,36px)] font-light text-charcoal tracking-tight">
            {H_TITLE}
          </h2>

          <div className="mt-3 inline-flex items-center gap-3 rounded-2xl border bg-cream-white/80 backdrop-blur px-4 py-3 border-warm-sage/30 shadow-sm">
            <div className="p-2 rounded-full bg-warm-sage/15">
              <Quote className="h-4 w-4 text-warm-sage" />
            </div>
            <span className="text-sm md:text-base text-charcoal/90">{TRANSITION}</span>
          </div>
        </div>

        {/* Narratif + Photo de coaching */}
        <div className={`grid md:grid-cols-2 gap-6 md:gap-10 items-start mb-8 md:mb-10 ${dir === "rtl" ? "md:flex-row-reverse" : ""}`}>
          <div>
            <p className="text-charcoal/90 leading-relaxed mb-3">{N_P1}</p>
            <p className="text-charcoal/90 leading-relaxed mb-4">{N_P2}</p>
            <ul className={`flex flex-col sm:flex-row ${dir === "rtl" ? "sm:flex-row-reverse" : ""} gap-3 sm:gap-4`}>
              {N_BULLETS.map((b, i) => (
                <li key={i} className="inline-flex items-center gap-2 rounded-full border border-warm-sage/30 bg-warm-sage/10 px-3 py-2 text-sm text-charcoal">
                  <CheckCircle className="h-4 w-4 text-warm-sage" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img
              src={COACH_IMG}
              onError={(e) => (e.currentTarget.src = FALLBACK)}
              alt={t("whychoose.coachImgAlt")}
              className="w-full h-64 md:h-[320px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 to-transparent" />
          </div>
        </div>

        {/* Carrousel : 1 carte pleine largeur */}
        <div className="mt-2 md:mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-warm-sage" />
              <h4 className="text-lg font-medium text-charcoal">{t("whychoose.carousel.title")}</h4>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => scrollBy(-1)} aria-label={t("prev")}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => scrollBy(1)} aria-label={t("next")}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div
            ref={trackRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none]"
            style={{ scrollbarWidth: "none" }}
          >
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <Card key={c.key} className={`min-w-full snap-start p-6 md:p-8 bg-gradient-to-br ${c.bg} ${c.border}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`h-5 w-5 ${c.tint}`} />
                    <span className="text-xs uppercase tracking-widest text-warm-gray">{c.badge}</span>
                  </div>
                  <h5 className="text-xl md:text-2xl font-light text-charcoal mb-4">{c.title}</h5>

                  {"intro" in c && (
                    <>
                      <div className="space-y-2 mb-3">
                        {c.intro.map((p, i) => (
                          <p key={i} className="text-sm text-charcoal/90">
                            {p}
                          </p>
                        ))}
                      </div>
                      <h6 className="text-sm font-medium text-charcoal mb-2">{c.listTitle}</h6>
                    </>
                  )}

                  {"list" in c && c.key !== "process" && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {c.list.map((x, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className={`h-4 w-4 mt-0.5 ${c.tint}`} />
                          <span>{x}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {"steps" in c && (
                    <>
                      <ol className="space-y-3 mb-3">
                        {c.steps.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            {i === 0 && <Target className={`h-4 w-4 mt-0.5 ${c.tint}`} />}
                            {i === 1 && <Brain className={`h-4 w-4 mt-0.5 ${c.tint}`} />}
                            {i === 2 && <Layers className={`h-4 w-4 mt-0.5 ${c.tint}`} />}
                            {i === 3 && <Shield className={`h-4 w-4 mt-0.5 ${c.tint}`} />}
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>
                      <p className="text-xs text-warm-gray italic">{c.note}</p>
                    </>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Micro-quiz (4 Q) */}
        <div className="mt-12 md:mt-16">
          <Card className="p-6 md:p-8 bg-gradient-to-br from-cream-white to-soft-gray/40 border-soft-taupe/30">
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="h-5 w-5 text-warm-sage" />
              <h4 className="text-lg md:text-xl font-medium text-charcoal">
                {t("whychoose.quiz.title")}
              </h4>
            </div>

            <div className="grid gap-4">
              {quizItems.map((q, i) => {
                const val = answers[i];
                return (
                  <div
                    key={i}
                    role="group"
                    aria-label={q}
                    className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-warm-sage/20 bg-warm-sage/5 p-3 pointer-events-auto"
                  >
                    <p className="text-sm text-charcoal">{q}</p>
                    <div className={`flex ${dir === "rtl" ? "sm:flex-row-reverse" : ""} gap-2`}>
                      <Button
                        type="button"
                        variant={val === true ? "default" : "outline"}
                        aria-pressed={val === true}
                        onClick={() => setAnswer(i, true)}
                        className={`h-9 px-4 ${val === true ? "bg-warm-sage text-cream-white" : ""}`}
                      >
                        {t("yes")}
                      </Button>
                      <Button
                        type="button"
                        variant={val === false ? "default" : "outline"}
                        aria-pressed={val === false}
                        onClick={() => setAnswer(i, false)}
                        className={`h-9 px-4 ${val === false ? "bg-rich-chocolate text-cream-white" : ""}`}
                      >
                        {t("no")}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Barre + score + statut */}
            <div className="mt-5 flex flex-col gap-2">
              <div className="h-2 w-full bg-soft-taupe/20 rounded-full overflow-hidden">
                <div className="h-full bg-warm-sage transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-warm-gray">
                  {t("whychoose.quiz.score")} : <strong>{yesCount}/4</strong>
                </span>
                <span className={`text-xs px-3 py-1 rounded-full ${signal.badge}`}>{signal.label}</span>
              </div>
            </div>

            <div className={`${dir === "rtl" ? "text-left" : "text-right"} mt-4`}>
              <Button
                size="lg"
                disabled={answered < quizItems.length}
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="group bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-7 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  {CTA_LABEL}
                  <Sparkles className="h-5 w-5 group-hover:rotate-180 transition-transform duration-500" />
                </span>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}